import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Close, Delete, Edit } from "@mui/icons-material";
import ClearFiltersButton from "./ClearFiltersButton";
import SavingOverlay from "./SavingOverlay";
import ConfirmModal from "./modals/ConfirmModal";
import { Toast } from "./Toast";
import { useToast } from "../hooks/useToast";
import { colors, inputs, typography } from "../theme/designSystem";
import { filterFieldSx, filterActionButtonSx, searchFieldSx } from "../theme/filterStyles";
import {
  PATIENT_LINK_NOTES_MAX_LENGTH,
  PatientLink,
  PatientLinkTypeOption,
  mapPatientLinkError,
  patientLinksService,
} from "../services/patientLinksService";
import {
  Institution,
  InstitutionCatalogs,
  mapInstitutionError,
} from "../services/institutionsService";
import { useAuth } from "../contexts/AuthContext";

/**
 * Aba Vínculos do cadastro do paciente (RN-553): onde ele estuda, trabalha, treina, é atendido ou
 * frequenta. Permissão própria `patient-links`; quem vê a aba também escreve.
 *
 * Encerrar um vínculo é preencher a data de fim — não existe botão de "desativar". Um flag
 * separado mentiria assim que alguém esquecesse de desmarcá-lo, e apagaria a pergunta que importa
 * depois: em que escola ele estava em março?
 */
interface Props {
  patientId: string;
}

const formatIsoDate = (iso: string | null): string =>
  iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "";

const periodOf = (link: PatientLink): string => {
  const inicio = formatIsoDate(link.startDate);
  const fim = formatIsoDate(link.endDate);
  if (inicio && fim) return `${inicio} a ${fim}`;
  if (inicio) return `Desde ${inicio}`;
  if (fim) return `Até ${fim}`;
  return "Sem período informado";
};

/** "Pública · Municipal · Educação" — o que a tela mostra ao encontrar a instituição. */
const classificationOf = (i: Institution | null): string => {
  if (!i) return "";
  const partes = [i.natureName, i.sphereName, i.segmentName].filter(Boolean);
  if (i.nonProfit === true) partes.push("sem fins lucrativos");
  return partes.join(" · ");
};

interface FormState {
  id: string | null;
  linkTypeId: string;
  institution: Institution | null;
  description: string;
  startDate: string;
  endDate: string;
  notes: string;
  contactName: string;
  contactPhone: string;
}

const emptyForm = (): FormState => ({
  id: null,
  linkTypeId: "",
  institution: null,
  description: "",
  startDate: "",
  endDate: "",
  notes: "",
  contactName: "",
  contactPhone: "",
});

const PatientLinksTab: React.FC<Props> = ({ patientId }) => {
  const { user } = useAuth();
  const entityId = user?.entityId || "";
  const { toast, showToast, hideToast } = useToast();

  const [links, setLinks] = useState<PatientLink[]>([]);
  const [types, setTypes] = useState<PatientLinkTypeOption[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [status, setStatus] = useState("current");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");

  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  // Inclusão rápida de instituição sem sair do vínculo (pedido do Hilton, 07/09). Só para quem
  // tem a permissão do cadastro: sem ela o clique morreria em 403.
  // Quem registra vínculo cadastra a instituição que faltou (RN-553k): a régua é a da ABA, não a
  // do cadastro administrativo. Sem isso o Profissional via a aba e não conseguia usá-la.
  const podeCadastrarInstituicao = true;
  const [novaOpen, setNovaOpen] = useState(false);
  const [nova, setNova] = useState({ name: "", natureId: "", sphereId: "", segmentId: "", phone: "" });
  const [novaSaving, setNovaSaving] = useState(false);
  const [novaError, setNovaError] = useState("");
  const [catalogs, setCatalogs] = useState<InstitutionCatalogs>({
    natures: [],
    spheres: [],
    segments: [],
    professionalTypes: [],
  });
  const [saveError, setSaveError] = useState("");
  const [toDelete, setToDelete] = useState<PatientLink | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError("");
    try {
      const result = await patientLinksService.getByPatient(patientId, status);
      if (seq !== loadSeqRef.current) return;
      setLinks(result);
    } catch {
      if (seq !== loadSeqRef.current) return;
      setError("Não foi possível carregar os vínculos deste paciente.");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [patientId, status]);

  useEffect(() => {
    load();
  }, [load]);

  // Catálogo e instituições carregam uma vez: alimentam o combo do modal e o filtro de tipo.
  useEffect(() => {
    patientLinksService.types().then(setTypes).catch(() => setTypes([]));
  }, []);

  const carregarInstituicoes = useCallback(async () => {
    if (!entityId) return;
    try {
      setInstitutions(await patientLinksService.institutions(entityId));
    } catch {
      setInstitutions([]);
    }
  }, [entityId]);

  useEffect(() => {
    carregarInstituicoes();
  }, [carregarInstituicoes]);

  // Catálogos só para quem pode cadastrar: quem não pode nem vê o botão
  useEffect(() => {
    if (!entityId || !podeCadastrarInstituicao) return;
    patientLinksService.institutionCatalogs(entityId).then(setCatalogs).catch(() => undefined);
  }, [entityId, podeCadastrarInstituicao]);

  const hasFilters = status !== "current" || !!typeFilter || !!search.trim();
  const clearFilters = () => {
    setStatus("current");
    setTypeFilter("");
    setSearch("");
  };

  // Tipo e texto recortam o que já veio; o status é que vai ao servidor.
  const term = search.trim().toLowerCase();
  const visible = links
    .filter((l) => !typeFilter || l.linkTypeId === typeFilter)
    .filter(
      (l) =>
        !term ||
        l.institutionName.toLowerCase().includes(term) ||
        (l.description ?? "").toLowerCase().includes(term) ||
        (l.contactName ?? "").toLowerCase().includes(term)
    );

  const openCreate = () => {
    setSaveError("");
    setForm(emptyForm());
  };

  const openEdit = (link: PatientLink) => {
    setSaveError("");
    setForm({
      id: link.id,
      linkTypeId: link.linkTypeId,
      institution:
        institutions.find((i) => i.id === link.institutionId) ??
        ({
          id: link.institutionId,
          name: link.institutionName,
          address: null,
          number: null,
          neighborhood: null,
          zipCode: null,
          phone: link.institutionPhone,
          email: null,
          director: null,
          supervisor: null,
          services: null,
          natureId: null,
          natureName: null,
          sphereId: null,
          sphereName: null,
          segmentId: null,
          segmentName: null,
          nonProfit: null,
          active: true,
          linkCount: 0,
        } as Institution),
      description: link.description ?? "",
      startDate: link.startDate ?? "",
      endDate: link.endDate ?? "",
      notes: link.notes ?? "",
      contactName: link.contactName ?? "",
      contactPhone: link.contactPhone ?? "",
    });
  };

  const closeModal = () => {
    if (saving) return;
    setForm(null);
    setSaveError("");
  };

  const handleSave = async () => {
    if (!form) return;
    if (!form.linkTypeId) {
      setSaveError("Informe o tipo de vínculo.");
      return;
    }
    if (!form.institution) {
      setSaveError("Escolha a instituição.");
      return;
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      setSaveError("A data de fim não pode ser anterior à de início.");
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      await patientLinksService.save({
        id: form.id,
        patientId,
        linkTypeId: form.linkTypeId,
        institutionId: form.institution.id,
        description: form.description.trim() || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        notes: form.notes.trim() || null,
        contactName: form.contactName.trim() || null,
        contactPhone: form.contactPhone.trim() || null,
      });
      showToast(form.id ? "Vínculo atualizado." : "Vínculo registrado.", "success");
      setSaving(false);
      setForm(null);
      load();
    } catch (e: any) {
      setSaving(false);
      setSaveError(mapPatientLinkError(e));
    }
  };

  const novaPublica = !!nova.natureId
    && catalogs.natures.some((n) => n.id === nova.natureId && n.isPublic);

  const gravarInstituicao = async () => {
    if (!nova.name.trim()) {
      setNovaError("Informe o nome.");
      return;
    }
    if (!nova.natureId || !nova.segmentId) {
      setNovaError("Informe natureza e segmento.");
      return;
    }
    if (novaPublica && !nova.sphereId) {
      setNovaError("Instituição pública precisa da esfera.");
      return;
    }

    setNovaSaving(true);
    setNovaError("");
    try {
      const id = await patientLinksService.quickAddInstitution({
        entityId,
        name: nova.name.trim(),
        natureId: nova.natureId,
        sphereId: novaPublica ? nova.sphereId : null,
        segmentId: nova.segmentId,
        phone: nova.phone.trim() || null,
      });

      // Recarrega e JÁ SELECIONA a nova no vínculo: quem cadastrou dali quer usá-la agora
      const lista = await patientLinksService.institutions(entityId);
      setInstitutions(lista);
      const criada = lista.find((i) => i.id === id) ?? null;
      if (criada && form) {
        setForm({ ...form, institution: criada });
      }
      setNovaOpen(false);
      setNova({ name: "", natureId: "", sphereId: "", segmentId: "", phone: "" });
      showToast("Instituição cadastrada.", "success");
    } catch (e: any) {
      setNovaError(mapInstitutionError(e));
    } finally {
      setNovaSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await patientLinksService.remove(toDelete.id);
      showToast("Vínculo excluído.", "success");
      setToDelete(null);
      load();
    } catch (e: any) {
      showToast(mapPatientLinkError(e), "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="tab-content-section">
      {/* Padrão das abas (RN-539): sem título; a regra vive no FAQ do cadastro. */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: "21px" }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Nome, descrição ou contato"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={searchFieldSx}
        />
        <TextField
          select
          size="small"
          label="Situação"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="current">Em curso</MenuItem>
          <MenuItem value="ended">Encerrados</MenuItem>
          <MenuItem value="all">Todos</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Tipo"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {types.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        <Tooltip title="Novo vínculo" arrow>
          <span style={{ marginLeft: "auto" }}>
            <IconButton onClick={openCreate} sx={filterActionButtonSx}>
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2, px: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
          <strong>{visible.length}</strong> {visible.length === 1 ? "vínculo" : "vínculos"}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : error ? (
        <Typography sx={{ color: colors.error, fontSize: "0.9rem", px: 1, py: 2 }}>{error}</Typography>
      ) : visible.length === 0 ? (
        <Typography sx={{ color: colors.textSecondary, fontSize: "0.9rem", textAlign: "center", py: 4 }}>
          Nenhum vínculo registrado para este paciente.
        </Typography>
      ) : (
        <Box className="c4u-table c4u-table--serial">
          <Box className="c4u-table__header">
            <Box className="admin-plans-header-cell" sx={{ flex: "2 1 240px", minWidth: 180 }}>
              Instituição/Profissional
            </Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 160px" }}>
              Tipo
            </Box>
            <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 180px", minWidth: 140 }}>
              Período
            </Box>
            <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "1 1 180px", minWidth: 140 }}>
              Contato
            </Box>
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px" }}>
              Situação
            </Box>
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px", justifyContent: "flex-end" }}>
              Ações
            </Box>
          </Box>

          <Box className="admin-plans-table-body">
            {visible.map((link) => (
              <Box key={link.id} className="c4u-table__row">
                <Box
                  className="admin-plans-cell admin-plans-name"
                  sx={{ flex: "2 1 240px", minWidth: 180, gap: 0.5, flexWrap: "wrap" }}
                  data-label="Instituição/Profissional"
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{link.institutionName}</Typography>
                  {link.description && (
                    <Typography sx={{ fontSize: 11, color: "#adb5bd", width: "100%" }}>
                      {link.description}
                    </Typography>
                  )}
                </Box>
                <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 160px" }} data-label="Tipo">
                  <Typography sx={{ fontSize: 13 }}>{link.linkTypeName}</Typography>
                </Box>
                <Box
                  className="admin-plans-cell c4u-col--lg"
                  sx={{ flex: "1 1 180px", minWidth: 140 }}
                  data-label="Período"
                >
                  <Typography sx={{ fontSize: 13 }}>{periodOf(link)}</Typography>
                </Box>
                <Box
                  className="admin-plans-cell c4u-col--xl"
                  sx={{ flex: "1 1 180px", minWidth: 140, gap: 0.5, flexWrap: "wrap" }}
                  data-label="Contato"
                >
                  <Typography sx={{ fontSize: 13 }}>{link.contactName || "—"}</Typography>
                  {link.contactPhone && (
                    <Typography sx={{ fontSize: 11, color: "#adb5bd", width: "100%" }}>
                      {link.contactPhone}
                    </Typography>
                  )}
                </Box>
                <Box className="admin-plans-cell" sx={{ flex: "0 0 110px" }} data-label="Situação">
                  <Chip
                    label={link.current ? "Em curso" : "Encerrado"}
                    size="small"
                    sx={{
                      color: "white",
                      fontWeight: 600,
                      backgroundColor: link.current ? colors.success : "#adb5bd",
                    }}
                  />
                </Box>
                <Box
                  className="admin-plans-cell admin-plans-actions"
                  sx={{ flex: "0 0 110px" }}
                  data-label="Ações"
                >
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                    <Tooltip title="Editar vínculo">
                      <IconButton
                        className="c4u-row-action"
                        size="small"
                        onClick={() => openEdit(link)}
                        sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
                      >
                        <Edit sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir vínculo registrado por engano">
                      <IconButton
                        className="c4u-row-action"
                        size="small"
                        onClick={() => setToDelete(link)}
                        sx={{ backgroundColor: "#dc3545", "&:hover": { backgroundColor: "#c82333" } }}
                      >
                        <Delete sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Dialog
        open={form !== null}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.3)", overflow: "hidden", position: "relative" },
        }}
      >
        <SavingOverlay open={saving} message="Gravando vínculo..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: "1.5rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontSize: "1.4rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}
          >
            {form?.id ? "Editar vínculo" : "Novo vínculo"}
          </Typography>
          <IconButton onClick={closeModal} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.5rem !important", paddingTop: "2rem !important" }}>
          {saveError && (
            <Box
              sx={{
                backgroundColor: "#fde2e4",
                color: colors.error,
                borderRadius: "8px",
                padding: "0.6rem 1rem",
                fontSize: "0.85rem",
                mb: 2,
              }}
            >
              {saveError}
            </Box>
          )}
          {form && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Tipo de vínculo"
                value={form.linkTypeId}
                onChange={(e) => setForm({ ...form, linkTypeId: e.target.value })}
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {types.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: 0 }}
                options={institutions}
                value={form.institution}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => setForm({ ...form, institution: value })}
                noOptionsText="Nenhuma instituição encontrada — cadastre em Instituições"
                // A classificação aparece na opção e abaixo do campo: aqui ela é INFORMAÇÃO,
                // não campo. Quem classifica a instituição é a tela de Instituições (RN-553c);
                // repetir os combos aqui criaria dois donos para o mesmo dado.
                renderOption={(props, option) => {
                  const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                  const classificacao = classificationOf(option);
                  return (
                    <li key={key} {...rest}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.9rem" }}>{option.name}</Typography>
                        {classificacao && (
                          <Typography sx={{ fontSize: "0.72rem", color: colors.textSecondary }}>
                            {classificacao}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Instituição/Profissional"
                    placeholder="Digite para buscar pelo nome"
                    required
                    size="small"
                    helperText={classificationOf(form.institution) || undefined}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        fontSize: inputs.default.labelFontSize,
                        color: inputs.default.labelColor,
                        backgroundColor: inputs.default.labelBackground,
                        padding: inputs.default.labelPadding,
                      },
                    }}
                  />
                )}
              />
              {podeCadastrarInstituicao && (
                <Tooltip title="Cadastrar instituição sem sair daqui" arrow>
                  <IconButton
                    onClick={() => {
                      setNovaError("");
                      setNovaOpen(true);
                    }}
                    sx={{
                      mt: 0.25,
                      backgroundColor: colors.primary,
                      color: "#fff",
                      "&:hover": { backgroundColor: colors.primaryHover },
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
              </Box>

              <TextField
                label="Descrição"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                size="small"
                inputProps={{ maxLength: 200 }}
                InputLabelProps={{ shrink: true }}
                placeholder="Ex.: 1ª série, aprendiz, natação 2x/semana"
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              />

              <TextField
                label="Início"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Fim"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                helperText="Em branco = vínculo em curso"
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Contato"
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                size="small"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
                placeholder="Quem se procura ali por este paciente"
              />
              <TextField
                label="Telefone do contato"
                value={form.contactPhone}
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                size="small"
                inputProps={{ maxLength: 20 }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Observação"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                multiline
                minRows={3}
                size="small"
                inputProps={{ maxLength: PATIENT_LINK_NOTES_MAX_LENGTH }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  gridColumn: { xs: "auto", sm: "1 / -1" },
                  "& textarea": { minHeight: "0 !important", border: "none !important", padding: "0 !important" },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.5rem", gap: 1 }}>
          <Button onClick={closeModal} disabled={saving} sx={{ color: colors.textSecondary, textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form?.linkTypeId || !form?.institution}
            sx={{ backgroundColor: colors.primary, textTransform: "none", "&:hover": { backgroundColor: "#029AAB" } }}
          >
            {form?.id ? "Salvar" : "Gravar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inclusão rápida: só o essencial. O resto (endereço, direção, serviços) se completa
          depois na tela de Instituições — aqui a pessoa está no meio de outra tarefa. */}
      <Dialog
        open={novaOpen}
        onClose={() => !novaSaving && setNovaOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={novaSaving} message="Cadastrando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem 1.25rem",
          }}
        >
          <Typography
            component="h3"
            sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}
          >
            Nova instituição
          </Typography>
          <IconButton onClick={() => setNovaOpen(false)} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {novaError && (
              <Box
                sx={{
                  backgroundColor: "#fde2e4",
                  color: colors.error,
                  borderRadius: "8px",
                  padding: "0.6rem 1rem",
                  fontSize: "0.85rem",
                }}
              >
                {novaError}
              </Box>
            )}
            <TextField
              label="Nome"
              value={nova.name}
              onChange={(e) => setNova({ ...nova, name: e.target.value })}
              required
              size="small"
              inputProps={{ maxLength: 150 }}
              InputLabelProps={{ shrink: true }}
              placeholder="Ex.: EMEF Vila Nova"
            />
            <TextField
              select
              label="Natureza"
              value={nova.natureId}
              onChange={(e) => setNova({ ...nova, natureId: e.target.value, sphereId: "" })}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>Selecione</em>
              </MenuItem>
              {catalogs.natures
                .filter((n) => n.active)
                .map((n) => (
                  <MenuItem key={n.id} value={n.id}>
                    {n.name}
                  </MenuItem>
                ))}
            </TextField>
            {novaPublica && (
              <TextField
                select
                label="Esfera"
                value={nova.sphereId}
                onChange={(e) => setNova({ ...nova, sphereId: e.target.value })}
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {catalogs.spheres
                  .filter((sp) => sp.active)
                  .map((sp) => (
                    <MenuItem key={sp.id} value={sp.id}>
                      {sp.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
            <TextField
              select
              label="Segmento"
              value={nova.segmentId}
              onChange={(e) => setNova({ ...nova, segmentId: e.target.value })}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>Selecione</em>
              </MenuItem>
              {catalogs.segments
                .filter((sg) => sg.active)
                .map((sg) => (
                  <MenuItem key={sg.id} value={sg.id}>
                    {sg.name}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              label="Telefone"
              value={nova.phone}
              onChange={(e) => setNova({ ...nova, phone: e.target.value })}
              size="small"
              inputProps={{ maxLength: 60 }}
              InputLabelProps={{ shrink: true }}
            />
            <Typography sx={{ fontSize: "0.78rem", color: colors.textSecondary }}>
              Endereço, direção e serviços podem ser completados depois, em Instituições.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button
            onClick={() => setNovaOpen(false)}
            variant="outlined"
            disabled={novaSaving}
            sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={gravarInstituicao}
            variant="contained"
            disabled={novaSaving || !nova.name.trim()}
            sx={{ textTransform: "none", backgroundColor: colors.primary }}
          >
            Cadastrar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!toDelete}
        title="Excluir vínculo"
        message={
          toDelete
            ? `O vínculo com "${toDelete.institutionName}" será excluído. Use isto só para registro feito por engano — para encerrar um vínculo que existiu, preencha a data de fim.`
            : ""
        }
        type="danger"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={deleting}
        onConfirm={handleDelete}
        onCancel={() => !deleting && setToDelete(null)}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
};

export default PatientLinksTab;
